import { setPageMeta } from '../seo'
import React, { useEffect } from 'react'
import SiteFooter from '../components/SiteFooter'

// The film's own page. In pre-production, so no trailer and no footage. The
// words are Elaine's, used as written. Centred throughout: there is not enough
// on the page yet for a left-aligned column to hold it together.

const team = [
  { name: 'Hillary O\u2019Sullivan', role: 'Pianist', image: 'portrait-hillary-osullivan.jpg' },
  { name: 'Elaine Lim', role: 'Director, Producer', image: 'portrait-elaine-lim.jpg' },
  { name: 'Ian Mun', role: 'Co-Director, Director of Photography', image: 'portrait-ian-mun.jpg' },
  { name: 'Nanang Sujana', role: 'Cinematographer', image: 'portrait-nanang-sujana.jpg' },
]

// The figures from the deck, rebuilt as type rather than a picture of a slide.
const figures = [
  {
    value: '~11,700',
    label: 'Sumatran orangutans remaining, a decline of 19.5% since 2011',
  },
  {
    value: '~700',
    label: 'Tapanuli orangutans, the rarest great ape on earth. 11% of the population lost to landslides and floods in November 2025',
  },
  {
    value: '~50%',
    label: 'of Sumatra\u2019s natural forest cover lost between 1985 and 2008. A super El Ni\u00f1o forecast for 2026 and 2027 may bring more',
  },
  {
    value: '1.6bn',
    label: 'tonnes of carbon held in the peat beneath the Sumatran forest floor',
  },
]

const HandsLikeOurs = () => {
  useEffect(() => {
    setPageMeta({
      title: 'Hands Like Ours | Orangutans and the last canopy',
      path: '/handslikeours',
      description: 'A young pianist journeys into Sumatra with a question, bringing her music into the heart of the rainforest. A short documentary in pre-production from White Room Studio, Singapore.',
      image: '/assets/hlo/leuser-rainforest-canopy.jpg',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: 'Hands Like Ours',
        alternateName: 'Hands Like Ours: Orangutans and the Last Canopy',
        url: 'https://www.thegirlandherpiano.com/handslikeours',
        image: 'https://www.thegirlandherpiano.com/assets/hlo/leuser-rainforest-canopy.jpg',
        description: 'A short documentary following Singaporean pianist Hillary O\u2019Sullivan into the Leuser Ecosystem of North Sumatra, the last place on earth where wild orangutans, elephants, tigers and rhinos share a forest. In pre-production; scheduled for 2027.',
        genre: ['Documentary', 'Environment'],
        inLanguage: 'en',
        countryOfOrigin: { '@type': 'Country', name: 'Singapore' },
        contentLocation: { '@type': 'Place', name: 'Leuser Ecosystem, North Sumatra, Indonesia' },
        director: [
          { '@type': 'Person', name: 'Elaine Lim' },
          { '@type': 'Person', name: 'Ian Mun' },
        ],
        producer: { '@type': 'Person', name: 'Elaine Lim' },
        actor: { '@type': 'Person', name: 'Hillary O\u2019Sullivan', url: 'https://www.thegirlandherpiano.com/hillary-osullivan' },
        productionCompany: { '@type': 'Organization', name: 'White Room Studio Pte Ltd', url: 'https://www.whiteroomstudio.com.sg/' },
        sameAs: ['https://handslikeours.com', 'https://handslikeoursfilm.com'],
      },
    })
    window.scrollTo(0, 0)

  }, [])

  return (
    <div className='App page-standard page-hlo'>
      <header
        className='hlo-hero'
        style={{ backgroundImage: "url('/assets/hlo/leuser-rainforest-canopy.jpg')" }}
      >
        <div className='hlo-hero-veil' aria-hidden='true' />
        <div className='hlo-hero-inner'>
          <img
            className='hlo-mark'
            src='/assets/hlo/hands-like-ours-mark.png'
            alt=''
            aria-hidden='true'
            width='560'
            height='560'
          />
          {/* The h1 is the longer line a search engine reads; the visible
              title stays as it is. */}
          <h1 className='visually-hidden'>Hands Like Ours: Sumatran orangutans and rainforests</h1>
          <p className='hlo-title'>Hands Like Ours</p>
          <p className='hlo-hero-sub'>Orangutans and the last canopy</p>
          <p className='hlo-status'>In pre-production</p>
        </div>
      </header>

      <main>
        <section className='hlo-logline' data-tone='dark'>
          <div className='content-container'>
            <p>
              A young pianist journeys into Sumatra with a question, bringing
              her music into the heart of the rainforest.
            </p>
          </div>
        </section>

        <section className='hlo-film' data-tone='dark'>
          <div className='content-container'>
            <p>
              Deep within the Leuser Ecosystem of North Sumatra, the ancient
              canopy is receding. The wild orangutans that have called these
              forests home for a million years are facing a rapidly changing
              landscape.
            </p>
            <p>
              Hands Like Ours follows Singaporean pianist Hillary
              O&rsquo;Sullivan as she explores our relationship with these
              incredible primates, the researchers dedicating their lives to
              protecting them, and the fragile ecosystem we share. The film
              documents an artistic exploration of habitat loss, using music to
              bridge the distance between human urban centers and the reality of
              the vanishing wild.
            </p>
          </div>
        </section>

        <section className='hlo-orang' data-tone='dark'>
          <img
            src='/assets/hlo/sumatran-orangutan-infant-band.jpg'
            alt='A Sumatran orangutan and her infant. Photograph: Ian Mun'
            width='1236'
            height='915'
            loading='lazy'
          />
        </section>

        <section className='hlo-fact' data-tone='dark'>
          <div className='content-container'>
            <h2>The Leuser Ecosystem</h2>
            <p>
              The last place on Earth where wild orangutans, elephants, tigers
              and rhinos still co-exist in a single shared forest.
              <span>The nearest wild orangutans to Singapore.</span>
            </p>
          </div>
        </section>

        <section className='hlo-figures' data-tone='dark'>
          <div className='content-container'>
            <ul className='hlo-figure-list'>
              {figures.map((f) => (
                <li key={f.value}>
                  <span className='hlo-figure-value'>{f.value}</span>
                  <span className='hlo-figure-label'>{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className='hlo-forest'
          data-tone='dark'
          style={{ backgroundImage: "url('/assets/hlo/leuser-rainforest-canopy.jpg')" }}
          aria-hidden='true'
        />

        <section className='hlo-why' data-tone='dark'>
          <div className='content-container'>
            <h2>Director&rsquo;s vision</h2>
            <blockquote>
              <p>
                This project is about a shared emotional inheritance. The forest
                is where all of us come from, and the orangutans are the part of
                that family that never left it. Singapore sits just two
                generations away from our own history of plantations and primary
                jungle, a memory our modern city has largely left behind.
              </p>
              <p>
                By bringing music directly into the Leuser Ecosystem,
                we want to collapse the distance between Singapore&rsquo;s urban
                landscape and the frontline of wildlife conservation. The
                Sumatran rainforest is close enough to be called our backyard,
                and our goal is to show why it must be cared for like home.
              </p>
            </blockquote>
            <cite>Elaine Lim, Director</cite>
          </div>
        </section>

        <section className='hlo-team' data-tone='dark'>
          <div className='content-container'>
            <h2>The team</h2>
            <ul className='hlo-crew'>
              {team.map((c) => (
                <li key={c.name}>
                  <img src={`/assets/hlo/${c.image}`} alt={c.name} width='560' height='560' loading='lazy' />
                  <span className='hlo-crew-name'>{c.name}</span>
                  <span className='hlo-crew-role'>{c.role}</span>
                </li>
              ))}
            </ul>
            <p className='hlo-credits'>
              Developed in collaboration with Yayasan Ekosistem Lestari (YEL)
              and the Sumatran Orangutan Conservation Programme (SOCP). Produced
              by White Room Studio, Singapore.
            </p>
          </div>
        </section>

        <section className='hlo-diary' data-tone='dark'>
          <div className='content-container'>
            <h2>Producer&rsquo;s diary</h2>
            <p>
              Notes from the making of the film, written on the road. The first
              entries will be written on the recce in North Sumatra.
            </p>
            <p className='hlo-diary-link'>
              <a href='/handslikeours/producers-diary'>Read the diary</a>
            </p>
          </div>
        </section>

        <section className='hlo-contact' data-tone='dark'>
          <div className='content-container'>
            <span className='hlo-rule' aria-hidden='true' />
            <h2>The forests are our home too</h2>
          </div>
        </section>
      </main>

      <SiteFooter
        mark='/assets/hlo/hands-like-ours-mark.png'
        markHover='/assets/hlo/hands-like-ours-mark.png'
      />
    </div>
  )
}

export default HandsLikeOurs
